"use client";
import "./_styles/global.scss";
import "@mantine/core/styles.css";
import "@mantine/nprogress/styles.css";
import "@mantine/dropzone/styles.css";
import "@mantine/dates/styles.css";
import "@mantine/carousel/styles.css";
import "@mantine/spotlight/styles.css";
import "@mantine/charts/styles.css";
// ‼️ import tiptap styles after core package styles
import "@mantine/tiptap/styles.css";
import { ColorSchemeScript, MantineProvider, createTheme } from "@mantine/core";
import { Inter } from "next/font/google";
import ClientWrapper from "./ClientWrapper";
import GoogleTag from "./_components/GoogleTag/GoogleTag";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useState } from "react";

const inter = Inter({ subsets: ["latin"] });

const theme = createTheme({
  colors: {
    primary: [
      "#d4eefd",
      "#aaddfc",
      "#7fccfa",
      "#55bbf9",
      "#2aaaf7",
      "#0099f6",
      "#007fcd",
      "#0066a4",
      "#004c7b",
      "#003352",
      "#001929",
    ],
    mountType: [
      "#d4eefd",
      "#010101",
      "#7fccfa",
      "#55bbf9",
      "#2aaaf7",
      "#0099f6",
      "#007fcd",
      "#0066a4",
      "#004c7b",
      "#003352",
      "#001929",
    ],
    serviceType: [
      "#fff0f6",
      "#ffdeeb",
      "#fcc2d7",
      "#faa2c1",
      "#f783ac",
      "#f06595",
      "#e64980",
      "#d6336c",
      "#c2255c",
      "#a61e4d",
      "#86184d",
    ],
  },
});

export default function RootLayout({
  children,
}: {
  children: React.ReactNode;
}) {
  // Must be created once and kept. This is a client layout, so a bare
  // `new QueryClient()` here would hand QueryClientProvider a different client
  // on every render of the layout, throwing away the cache and forcing every
  // mounted query in the app to resubscribe and refire at once. useState keeps
  // it stable per client, and still gives each SSR request its own cache
  // rather than sharing one between users.
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: { queries: { refetchOnWindowFocus: false, retry: 2 } },
      })
  );

  return (
    <html lang="en" data-mantine-color-scheme="light">
      <head>
        <ColorSchemeScript defaultColorScheme="light" />
        <meta charSet="UTF-8" />
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1.0, user-scalable=no"
        />
        <title>MountMonitor</title>
        <meta name="theme-color" content="#ffffff" />
        <link
          rel="icon"
          type="image/png"
          href="/favicon-96x96.png"
          sizes="96x96"
        />
        <link rel="icon" type="image/svg+xml" href="/favicon.svg" />
        <link rel="shortcut icon" href="/favicon.ico" />
        <link
          rel="apple-touch-icon"
          sizes="180x180"
          href="/apple-touch-icon.png"
        />
        <meta name="apple-mobile-web-app-title" content="MountMonitor" />
        <link rel="manifest" href="/site.webmanifest" />
        <GoogleTag />
      </head>
      <body className={inter.className}>
        <QueryClientProvider client={queryClient}>
          <MantineProvider theme={theme} defaultColorScheme="light">
            <ClientWrapper>{children}</ClientWrapper>
          </MantineProvider>
        </QueryClientProvider>
      </body>
    </html>
  );
}
